// ./src/utils/api/SavedPlaceService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const SAVED_PLACE_FIELDS = `
  id
  name
  normalizedName
  placeType
  isPrivate
  whereDone
  address1
  address2
  city
  postalCode
  stateProvince
  country
  countryId
  latitude
  longitude
  radiusM
  useCount
  lastUsedAt
  orderNo
  status
  version
  createdBy
  updatedBy
  createdAt
  updatedAt
  userCreated {
    id
    firstName
    lastName
  }
  userUpdated {
    id
    firstName
    lastName
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const SAVED_PLACE_LIST_QUERY = gql`
  query SavedPlaceList($filter: SavedPlaceFilter, $params: PaginationAndSorting) {
    savedPlaceList(filter: $filter, params: $params) {
      code
      data {
        ${SAVED_PLACE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SAVED_PLACE_LIST_BY_PROXIMITY_QUERY = gql`
  query SavedPlaceListByProximity($proximity: SavedPlaceProximityInput, $filter: SavedPlaceFilter) {
    savedPlaceListByProximity(proximity: $proximity, filter: $filter) {
      code
      data {
        ${SAVED_PLACE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SAVED_PLACE_GET_QUERY = gql`
  query SavedPlaceGet($id: ID) {
    savedPlaceGet(id: $id) {
      code
      data {
        ${SAVED_PLACE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SAVED_PLACE_GET_MANY_QUERY = gql`
  query SavedPlaceGetMany($ids: [ID]) {
    savedPlaceGetMany(ids: $ids) {
      code
      data {
        ${SAVED_PLACE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const SAVED_PLACE_CREATE_MUTATION = gql`
  mutation SavedPlaceCreate($params: SavedPlaceInput) {
    savedPlaceCreate(params: $params) {
      code
      data {
        ${SAVED_PLACE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SAVED_PLACE_UPDATE_MUTATION = gql`
  mutation SavedPlaceUpdate($where: SavedPlaceWhereInput, $params: SavedPlaceInput) {
    savedPlaceUpdate(where: $where, params: $params) {
      code
      data {
        ${SAVED_PLACE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SAVED_PLACE_REMOVE_MUTATION = gql`
  mutation SavedPlaceRemove($where: SavedPlaceWhereInput) {
    savedPlaceRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SAVED_PLACE_REMOVE_MANY_MUTATION = gql`
  mutation SavedPlaceRemoveMany($where: [SavedPlaceWhereInput]) {
    savedPlaceRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/**
 * Place type constants
 */
export const PLACE_TYPE = {
  HOME: 'home',
  OFFICE: 'office',
  HQ: 'hq',
  BRANCH: 'branch',
  POS: 'pos',
  WAREHOUSE: 'warehouse',
  WHOLESALE: 'wholesale',
  CLIENT: 'client',
  GAS_STATION: 'gas_station',
  MECHANIC: 'mechanic',
  STORE: 'store',
  OTHER: 'other',
} as const;

export type PlaceTypeValue = (typeof PLACE_TYPE)[keyof typeof PLACE_TYPE];

/**
 * Saved place status constants
 */
export const SAVED_PLACE_STATUS = {
  ACTIVE: 100,
  INACTIVE: 200,
} as const;

export type SavedPlaceStatusValue = (typeof SAVED_PLACE_STATUS)[keyof typeof SAVED_PLACE_STATUS];

export interface SavedPlace {
  id?: string | undefined;
  /** Display label: Home, Acme Corp, Shell on 5th */
  name?: string | undefined;
  /** Lowercase name for case-insensitive operations */
  normalizedName?: string | undefined;
  /** Place type: home, office, client, gas_station, mechanic, store, other */
  placeType?: PlaceTypeValue | undefined;
  /** When true, only visible to the user who created it */
  isPrivate?: boolean | undefined;
  /** Place/business name (maps to expense_bases.where_done) */
  whereDone?: string | undefined;
  /** Street address */
  address1?: string | undefined;
  /** PO Box, Apartment, Suite, etc. */
  address2?: string | undefined;
  city?: string | undefined;
  postalCode?: string | undefined;
  stateProvince?: string | undefined;
  /** Country full name */
  country?: string | undefined;
  /** Country code: US, CA, FR, RU, etc */
  countryId?: string | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  /** Auto-match radius in meters (default: 150) */
  radiusM?: number | undefined;
  /** Number of times this place has been selected */
  useCount?: number | undefined;
  /** Last time this place was selected */
  lastUsedAt?: string | undefined;
  orderNo?: number | undefined;
  status?: SavedPlaceStatusValue | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
}

export interface SavedPlaceInput {
  id?: string | null | undefined;
  /** Display label */
  name?: string | null | undefined;
  /** Place type: home, office, client, gas_station, mechanic, store, other */
  placeType?: PlaceTypeValue | null | undefined;
  /** When true, only visible to the creator */
  isPrivate?: boolean | null | undefined;
  /** Place/business name */
  whereDone?: string | null | undefined;
  /** Street address */
  address1?: string | null | undefined;
  /** PO Box, Apartment, Suite, etc. */
  address2?: string | null | undefined;
  city?: string | null | undefined;
  postalCode?: string | null | undefined;
  stateProvince?: string | null | undefined;
  country?: string | null | undefined;
  /** Country code: US, CA, FR, RU, etc */
  countryId?: string | null | undefined;
  latitude?: number | null | undefined;
  longitude?: number | null | undefined;
  /** Auto-match radius in meters (default: 150) */
  radiusM?: number | null | undefined;
  orderNo?: number | null | undefined;
  status?: SavedPlaceStatusValue | null | undefined;
}

export interface SavedPlaceFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  /** Filter by place type(s) */
  placeType?: PlaceTypeValue[] | null | undefined;
  /** Filter by privacy flag */
  isPrivate?: boolean | null | undefined;
  /** Filter by creator */
  createdBy?: string[] | null | undefined;
  /** Filter by status */
  status?: SavedPlaceStatusValue[] | null | undefined;
  /** Search in name, whereDone, address1, city */
  searchKeyword?: string | null | undefined;
}

export interface SavedPlaceProximityInput {
  /** Current GPS latitude */
  latitude: number;
  /** Current GPS longitude */
  longitude: number;
  /** Override default radius in meters */
  radiusM?: number | null | undefined;
}

export interface SavedPlaceWhereInput {
  id?: string | undefined;
}

// =============================================================================
// Helper Functions
// =============================================================================


/**
 * Format a saved place address into a single string
 */
export function formatSavedPlaceAddress(place: SavedPlace): string {
  const parts: string[] = [];

  if (place.address1) parts.push(place.address1);
  if (place.address2) parts.push(place.address2);
  if (place.city) parts.push(place.city);
  if (place.stateProvince) parts.push(place.stateProvince);
  if (place.postalCode) parts.push(place.postalCode);
  if (place.country) parts.push(place.country);

  return parts.join(', ');
}

/**
 * Get preferred place types order for a given travel type.
 * Returns place types sorted by relevance for the travel type,
 * with the most relevant types first.
 */
export function getPlaceTypePriority(travelType?: string): PlaceTypeValue[] {
  switch (travelType) {
    case 'business':
      return [
        PLACE_TYPE.OFFICE,
        PLACE_TYPE.HQ,
        PLACE_TYPE.BRANCH,
        PLACE_TYPE.CLIENT,
        PLACE_TYPE.POS,
        PLACE_TYPE.WAREHOUSE,
        PLACE_TYPE.WHOLESALE,
        PLACE_TYPE.OTHER,
        PLACE_TYPE.HOME,
        PLACE_TYPE.GAS_STATION,
        PLACE_TYPE.MECHANIC,
        PLACE_TYPE.STORE,
      ];
    case 'commute':
      return [
        PLACE_TYPE.HOME,
        PLACE_TYPE.OFFICE,
        PLACE_TYPE.HQ,
        PLACE_TYPE.BRANCH,
        PLACE_TYPE.OTHER,
        PLACE_TYPE.GAS_STATION,
        PLACE_TYPE.POS,
        PLACE_TYPE.CLIENT,
        PLACE_TYPE.WAREHOUSE,
        PLACE_TYPE.WHOLESALE,
        PLACE_TYPE.MECHANIC,
        PLACE_TYPE.STORE,
      ];
    case 'personal':
      return [
        PLACE_TYPE.HOME,
        PLACE_TYPE.OTHER,
        PLACE_TYPE.STORE,
        PLACE_TYPE.GAS_STATION,
        PLACE_TYPE.MECHANIC,
        PLACE_TYPE.WAREHOUSE,
        PLACE_TYPE.WHOLESALE,
        PLACE_TYPE.OFFICE,
        PLACE_TYPE.HQ,
        PLACE_TYPE.BRANCH,
        PLACE_TYPE.CLIENT,
        PLACE_TYPE.POS,
      ];
    default:
      return [
        PLACE_TYPE.HOME,
        PLACE_TYPE.OFFICE,
        PLACE_TYPE.HQ,
        PLACE_TYPE.BRANCH,
        PLACE_TYPE.CLIENT,
        PLACE_TYPE.POS,
        PLACE_TYPE.GAS_STATION,
        PLACE_TYPE.WAREHOUSE,
        PLACE_TYPE.WHOLESALE,
        PLACE_TYPE.MECHANIC,
        PLACE_TYPE.STORE,
        PLACE_TYPE.OTHER,
      ];
  }
}

/**
 * Sort saved places by place type priority for a given travel type,
 * then by useCount (desc), then by name (asc).
 */
export function sortSavedPlacesByRelevance(
  places: SavedPlace[],
  travelType?: string,
): SavedPlace[] {
  const priority = getPlaceTypePriority(travelType);

  return [...places].sort((a, b) => {
    // Sort by place type priority
    const aPriority = priority.indexOf(a.placeType || PLACE_TYPE.OTHER);
    const bPriority = priority.indexOf(b.placeType || PLACE_TYPE.OTHER);

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // Within same type, sort by use count (descending)
    const aCount = a.useCount || 0;
    const bCount = b.useCount || 0;

    if (aCount !== bCount) {
      return bCount - aCount;
    }

    // Finally, sort by name (ascending)
    return (a.name || '').localeCompare(b.name || '');
  });
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Saved place service that handles all saved place-related API calls.
 * Supports CRUD operations plus proximity search.
 */
export class SavedPlaceService {
  /**
   * Get a list of saved places with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: SavedPlaceFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(SAVED_PLACE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Find saved places near GPS coordinates, ordered by distance.
   * Used for auto-selecting places when starting/ending travels.
   */
  static async listByProximity(
    proximity: SavedPlaceProximityInput,
    filter?: SavedPlaceFilter,
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(SAVED_PLACE_LIST_BY_PROXIMITY_QUERY, {
      proximity,
      filter: filter || {},
    });
    return result;
  }

  /**
   * Get a single saved place by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(SAVED_PLACE_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple saved places by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(SAVED_PLACE_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new saved place
   */
  static async create(params: SavedPlaceInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(SAVED_PLACE_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing saved place
   */
  static async update(
    where: SavedPlaceWhereInput,
    params: SavedPlaceInput,
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(SAVED_PLACE_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a saved place (soft delete)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(SAVED_PLACE_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple saved places (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(SAVED_PLACE_REMOVE_MANY_MUTATION, {
      where,
    });
    return result;
  }
}